import { SettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'

import { getWaitForElementToAppearPromise } from './interruptiblePromises'
import { evaluateXpath } from './interruptibleXpath'
import { remoteLog } from '../utils/remoteLog'

type GetElementOptions = {
  /**
   * This allows us to turn off "happy path" remote logging for automations
   * that generate a ton of repetitive and unhelpful logs, like Facebook
   * unfriending. Errors will always be remote logged.
   */
  noDebug?: boolean
  /**
   * Timeout in milliseconds. If not specified, timeout is 5000ms.
   */
  timeout?: number
}

/**
 * Waits 5s (or the specified timeout in milliseconds) for an element to
 * appear, and remote logs an error and throws if it isn't found
 */
export const getElement = async <T>(
  context: Node,
  xpath: string,
  options?: GetElementOptions,
): Promise<T> => {
  if (!options?.noDebug) {
    remoteLog.debug(`getElement: ${xpath}`)
  }
  const element = (await getWaitForElementToAppearPromise(
    context || document,
    (node) => evaluateXpath(node, xpath)[0],
    options?.timeout ? options.timeout : 5000,
  )) as T

  if (!element) {
    const message = `getElement: Failed to get ${xpath}`
    remoteLog.error(message)
    throw new Error(message)
  }

  return element
}

/**
 * Waits 5s (or the specified timeout in milliseconds) for an element to
 * appear, and returns null if it isn't found
 */
export const getOptionalElement = async <T>(
  context: Node,
  xpath: string,
  options?: GetElementOptions,
): Promise<T | null> => {
  if (!options?.noDebug) {
    remoteLog.debug(`getOptionalElement: ${xpath}`)
  }
  const element = (await getWaitForElementToAppearPromise(
    context || document,
    (node) => evaluateXpath(node, xpath)[0],
    options?.timeout ? options.timeout : 5000,
    // Do not throw
    false,
  )) as T

  if (!element) {
    return null
  }

  return element
}

export function shouldUpdateSetting(
  settingsSelected,
  initialValues,
  settingKey: string,
  getIsFulfilledFn: (key) => (initialValues: { [key: string]: any }) => boolean,
): boolean {
  return (
    settingsSelected?.[settingKey] &&
    initialValues?.[settingKey] !== null &&
    initialValues?.[settingKey] !== undefined &&
    !getIsFulfilledFn(settingKey)(initialValues)
  )
}

/**
 * Function to read initial values that may be saved in different formats
 * New standard is that all initial values keys are of type SettingKey
 * However, still support reading old keys or no keys for backwards compatibility
 */
export function getInitialValueBySettingKey(
  initialValues: { [key: string]: any },
  settingKey: SettingKey,
  oldSettingKey?: string,
) {
  if (oldSettingKey) {
    return initialValues?.[settingKey] || initialValues?.[oldSettingKey]
  }

  return initialValues?.[settingKey] || initialValues
}
